import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./css/ScrollDetacher.css";
import { DockPanelHandle } from "./DockPanel/DockPanel";

interface ScrollDetacherProps {
    id: string;
    alternativeContent: React.ReactNode;
    placeholder?: React.ReactNode;
    placeholderBackground?: string;
    dockPanel: React.RefObject<DockPanelHandle | null>;
    children: React.ReactNode;
}

export const ScrollDetacher: React.FC<ScrollDetacherProps> = ({
    id,
    alternativeContent,
    placeholder,
    placeholderBackground,
    dockPanel,
    children
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [detached, setDetached] = useState(false);

    // Track the host element returned by DockPanel
    const hostRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                const isVisible = entry.isIntersecting;

                if (isVisible && detached && dockPanel?.current) {
                    dockPanel.current.remove(id);
                    hostRef.current = null;
                    setDetached(false);
                }

                if (!isVisible && !detached && dockPanel?.current) {
                    // Ask DockPanel for a host element
                    hostRef.current = dockPanel.current.getHostElement(id);
                    setDetached(true);
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(el);

        return () => {
            observer.disconnect();
            if (detached && dockPanel?.current) {
                dockPanel.current.remove(id);
            }
        };
    }, [detached, dockPanel, id]);

    return (
        <>
            <div
                ref={containerRef}
                className="sd-container"
                style={
                    placeholderBackground
                        ? { ["--sd-placeholder-bg" as any]: placeholderBackground }
                        : undefined
                }
            >
                <div className="sd-content">{children}</div>

                <div className={`sd-placeholder ${detached ? "sd-visible" : ""}`}>
                    <div className="sd-placeholder-content">
                        {placeholder}
                    </div>
                </div>
            </div>

            {detached && hostRef.current &&
                ReactDOM.createPortal(
                    alternativeContent,
                    hostRef.current
                )}
        </>
    );
};
